import { memo, useMemo } from 'react';
import CountUpModule from 'react-countup';
import { Badge } from 'reactstrap';
import { formatCurrency } from '../../utils/formatters';

// react-countup is a CJS package (`exports.default = CountUp`) and Vite's
// dev-mode pre-bundler sometimes hands back the whole module namespace
// rather than unwrapping `.default`, which crashes with "got: object".
// Rollup's prod build doesn't have the issue, but the defensive unwrap
// keeps both paths working without a vite.config tweak.
const CountUp = CountUpModule.default ?? CountUpModule;

// Budget-health traffic light on ActualCost vs EstimatedCost.
//   ≤80%   → On Track   (green)
//   80-100 → At Risk    (amber)
//   >100%  → Over Budget (red)
// No budget set (estimatedCost null/0) is its own grey state so the tile
// doesn't misleadingly show "On Track" when there's nothing to track against.
export function budgetHealth(actualCost, estimatedCost) {
    if (!estimatedCost || estimatedCost <= 0) {
        return { label: 'No budget set', tone: 'secondary', emoji: '⚪' };
    }
    const ratio = (actualCost ?? 0) / estimatedCost;
    if (ratio > 1)    return { label: 'Over Budget', tone: 'danger',  emoji: '🔴' };
    if (ratio >= 0.8) return { label: 'At Risk',     tone: 'warning', emoji: '🟠' };
    return              { label: 'On Track',         tone: 'success', emoji: '🟢' };
}

// Revenue-vs-quote chip. Mirrors budgetHealth but the polarity is flipped —
// over the quote is good, under it is flagged. Binary (above/under) because
// there's no "at risk" zone for revenue: you either hit the quote or you
// didn't. Grey state when there's no quote to compare against.
export function revenueHealth(actualRevenue, quotedRevenue) {
    if (!quotedRevenue || quotedRevenue <= 0) {
        return { label: 'No estimate', tone: 'secondary', emoji: '⚪' };
    }
    const ratio = (actualRevenue ?? 0) / quotedRevenue;
    if (ratio >= 1) return { label: 'Above Estimate', tone: 'success', emoji: '🟢' };
    return            { label: 'Under Estimate', tone: 'warning', emoji: '🟠' };
}

// Shared CountUp props — preserveValue + redraw=false means SignalR-driven
// cache refreshes animate old → new rather than restarting from 0 each time.
const COUNT_UP = { duration: 1.2, separator: ',', preserveValue: true, redraw: false };

// One tile. `tone` ('success' | 'warning' | 'danger' | null) colours the
// value text so negative profit / margin / over-budget cost restates the
// signal at the numeric level. `currency` (ISO 4217 code) routes the
// animated number through Intl.NumberFormat via react-countup's
// `formattingFn`; non-money tiles (Margin) pass `suffix` + `decimals`
// instead and leave currency undefined.
const KpiTile = memo(function KpiTile({ label, value, currency = null, suffix = '', decimals = 2, tone = null, badge = null }) {
    const toneClass = tone ? `text-${tone}` : '';
    // Stable reference — CountUp compares props by reference, so a new inline
    // function on every parent render restarts the animation even when the
    // value hasn't changed. useMemo recreates only when currency changes.
    const formattingFn = useMemo(
        () => currency ? (v) => formatCurrency(v, currency) : undefined,
        [currency],
    );
    return (
        <div className="surface h-100 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <div className="text-muted small text-uppercase">{label}</div>
                {badge}
            </div>
            <div className={`fs-3 fw-bold ${toneClass}`}>
                <CountUp
                    {...COUNT_UP}
                    end={value}
                    suffix={formattingFn ? '' : suffix}
                    decimals={decimals}
                    formattingFn={formattingFn}
                />
            </div>
        </div>
    );
});

// Five-tile KPI strip. Uses Bootstrap's `row-cols-*` flex-equal layout so
// all five tiles share row width on xl+, collapse to 2-per-row on md, and
// stack on xs. No magic column counting — the class handles the wrap.
// memo: the job reference from React Query's cache is stable when only modal
// state changes (Add Expense / Add Invoice toggles), so the entire strip and
// its CountUp animations can skip the re-render entirely.
export default memo(function JobKpiStrip({ job }) {
    const quotedRevenue = job.quotedRevenue ?? 0;
    const actualRevenue = job.actualRevenue ?? 0;
    const estimatedCost = job.estimatedCost ?? 0;
    const actualCost    = job.actualCost    ?? 0;
    const profit        = actualRevenue - actualCost;
    // Guard against div-by-zero AND against runaway percentages when revenue
    // is tiny — with 0 revenue the only honest number is 0.
    const margin = actualRevenue > 0 ? (profit / actualRevenue) * 100 : 0;

    const health = budgetHealth(actualCost, estimatedCost);
    const healthBadge = (
        <Badge color={health.tone} pill className="d-inline-flex align-items-center gap-1">
            <span>{health.emoji}</span>{health.label}
        </Badge>
    );

    const revHealth = revenueHealth(actualRevenue, quotedRevenue);
    const revenueBadge = (
        <Badge color={revHealth.tone} pill className="d-inline-flex align-items-center gap-1">
            <span>{revHealth.emoji}</span>{revHealth.label}
        </Badge>
    );

    const currency = job.currency ?? 'GBP';

    return (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-6 g-3 mb-4">
            <div className="col"><KpiTile label="Est. Revenue" value={quotedRevenue} currency={currency} /></div>
            <div className="col">
                <KpiTile
                    label="Actual Revenue"
                    value={actualRevenue}
                    currency={currency}
                    tone={revHealth.tone === 'secondary' ? null : revHealth.tone}
                    badge={revenueBadge}
                />
            </div>
            <div className="col"><KpiTile label="Est. Cost"   value={estimatedCost} currency={currency} /></div>
            <div className="col">
                <KpiTile
                    label="Actual Cost"
                    value={actualCost}
                    currency={currency}
                    tone={health.tone === 'secondary' ? null : health.tone}
                    badge={healthBadge}
                />
            </div>
            <div className="col">
                <KpiTile label="Profit" value={profit} currency={currency} tone={profit < 0 ? 'danger' : null} />
            </div>
            <div className="col">
                <KpiTile label="Margin" value={margin} suffix="%" decimals={1}
                         tone={margin < 0 ? 'danger' : null} />
            </div>
        </div>
    );
});
